import React, { useState, useEffect } from 'react';
import { config } from '../../config';
import './scss/StorageStatus.scss';

const StorageStatus = ({ workspaceId }) => {
    const [storageData, setStorageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showUpgradeOptions, setShowUpgradeOptions] = useState(false);

    useEffect(() => {
        fetchStorageStatus();
    }, [workspaceId]);

    const fetchStorageStatus = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${config.API_URI}/v1/appsumo/workspace/${workspaceId}/storage`, {
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setStorageData(data);
            } else {
                setError('Failed to fetch storage status');
            }
        } catch (error) {
            setError('Error fetching storage status');
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpgradeClick = (upgrade) => {
        // This would integrate with payment system
        console.log('Upgrade requested:', upgrade);

        if (upgrade.type === 'storage_addon' || upgrade.type === 'mega_storage') {
            alert(`${upgrade.name} would cost ${upgrade.price}. Payment integration would be implemented here.`);
        } else if (upgrade.tier) {
            alert(`Upgrade to ${upgrade.tier} would cost $${upgrade.upgradeCost}. Payment integration would be implemented here.`);
        } else {
            alert(`Switch to ${upgrade.plan} plan. Redirecting to billing page...`);
            window.location.href = `/w/${workspaceId}/billing`;
        }
    };

    const formatStorage = (gb) => {
        if (gb < 1) {
            return `${(gb * 1024).toFixed(0)}MB`;
        }
        return `${gb.toFixed(1)}GB`;
    };

    if (loading) {
        return (
            <div className="storage-status loading">
                <div className="loading-spinner"></div>
                <p>Loading storage information...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="storage-status error">
                <p>{error}</p>
                <button onClick={fetchStorageStatus}>Retry</button>
            </div>
        );
    }

    if (!storageData) {
        return null;
    }

    const { storageStatus, upgradeOptions, subscription } = storageData;

    return (
        <div className="storage-status">
            {/* Storage Usage Display */}
            <div className="storage-overview">
                <h3>Storage Usage</h3>
                <div className="storage-bar-container">
                    <div className="storage-info">
                        <span className="usage">
                            {formatStorage(storageStatus.usage)} used
                        </span>
                        <span className="limit">
                            {storageStatus.isUnlimited ? 'Unlimited' : `of ${formatStorage(storageStatus.limit)}`}
                        </span>
                    </div>
                    {!storageStatus.isUnlimited && (
                        <div className="storage-bar">
                            <div 
                                className={`storage-fill ${storageStatus.isOverLimit ? 'over-limit' : storageStatus.isNearLimit ? 'near-limit' : ''}`}
                                style={{ width: `${Math.min(storageStatus.usagePercentage, 100)}%` }}
                            ></div>
                        </div>
                    )}
                </div>

                {storageStatus.isOverLimit && (
                    <div className="storage-warning error">
                        ⚠️ Storage limit exceeded! Please upgrade or delete files to continue uploading.
                    </div>
                )}

                {storageStatus.isNearLimit && !storageStatus.isOverLimit && (
                    <div className="storage-warning warning">
                        ⚠️ Approaching storage limit. Consider upgrading your plan.
                    </div>
                )}
            </div>

            {/* Pro Plan Notice */}
            {subscription && ['Standard', 'Premium', 'Enterprise'].includes(subscription.planType) && (
                <div className="pro-plan-notice">
                    <h3>🎉 You're on a Pro Plan!</h3>
                    <p>Your workspace is on the <strong>{subscription.planType}</strong> plan with unlimited storage and seats.</p>
                    <div className="pro-benefits">
                        <h4>Your Pro Benefits:</h4>
                        <ul>
                            <li>✅ Unlimited storage</li>
                            <li>✅ Unlimited team seats</li>
                            <li>✅ All premium features</li>
                            <li>✅ Priority support</li>
                            {subscription.planType === 'Premium' && (
                                <>
                                    <li>✅ Advanced analytics</li>
                                    <li>✅ Data export</li>
                                </>
                            )}
                        </ul>
                    </div>
                    <p className="pro-note">
                        AppSumo codes cannot be redeemed on Pro workspaces. If you have an AppSumo code,
                        you can create a new workspace or contact support for migration assistance.
                    </p>
                </div>
            )}

            {/* Upgrade Options for AppSumo Users */}
            {upgradeOptions && subscription?.planType === 'AppSumo' && (
                <div className="upgrade-section">
                    <div className="upgrade-header">
                        <h3>Need More Storage or Seats?</h3>
                        <p>You're currently on AppSumo {upgradeOptions.currentTier.replace('tier', 'Tier ')}</p>
                        {upgradeOptions.isMaxTier && (
                            <div className="max-tier-notice">
                                🎉 You're on the highest AppSumo tier! For unlimited storage, consider our regular plans.
                            </div>
                        )}
                    </div>

                    <button 
                        className="show-upgrades-btn"
                        onClick={() => setShowUpgradeOptions(!showUpgradeOptions)}
                    >
                        {showUpgradeOptions ? 'Hide' : 'Show'} Upgrade Options
                    </button>

                    {showUpgradeOptions && (
                        <div className="upgrade-options">
                            {/* Recommendations for Tier 3 users */}
                            {upgradeOptions.recommendations && upgradeOptions.recommendations.length > 0 && (
                                <div className="upgrade-recommendations">
                                    {upgradeOptions.recommendations.map((recommendation, index) => (
                                        <div key={index} className="recommendation-section">
                                            <h4>{recommendation.title}</h4>
                                            <p className="recommendation-description">{recommendation.description}</p>
                                            {recommendation.note && (
                                                <p className="recommendation-note">{recommendation.note}</p>
                                            )}
                                            <div className="upgrade-cards">
                                                {recommendation.options.map((option, idx) => (
                                                    <div key={idx} className={`upgrade-card ${option.type || 'regular'}`}>
                                                        <div className="upgrade-header">
                                                            <h5>{option.name || option.plan}</h5>
                                                            <div className="upgrade-price">
                                                                {option.price || `$${option.upgradeCost}`}
                                                            </div>
                                                            <div className="upgrade-subtitle">
                                                                {option.description || 'Per month'}
                                                            </div>
                                                        </div>
                                                        <ul className="upgrade-benefits">
                                                            {option.benefits.map((benefit, bidx) => (
                                                                <li key={bidx}>✓ {benefit}</li>
                                                            ))}
                                                        </ul>
                                                        {option.note && (
                                                            <div className="upgrade-note">{option.note}</div>
                                                        )}
                                                        <button
                                                            className="upgrade-btn"
                                                            onClick={() => handleUpgradeClick(option)}
                                                        >
                                                            {option.type === 'storage_addon' || option.type === 'mega_storage'
                                                                ? 'Add Storage'
                                                                : option.tier
                                                                    ? 'Upgrade Now'
                                                                    : `Switch to ${option.plan}`
                                                            }
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* AppSumo Tier Upgrades (for Tier 1 & 2) */}
                            {upgradeOptions.hasAppSumoUpgrades && (
                                <div className="appsumo-upgrades">
                                    <h4>AppSumo Tier Upgrades</h4>
                                    <div className="upgrade-cards">
                                        {upgradeOptions.appSumoUpgrades.map((upgrade, index) => (
                                            <div key={index} className="upgrade-card appsumo">
                                                <div className="upgrade-header">
                                                    <h5>Upgrade to {upgrade.tier.replace('tier', 'Tier ')}</h5>
                                                    <div className="upgrade-price">${upgrade.upgradeCost}</div>
                                                    <div className="upgrade-subtitle">One-time payment</div>
                                                </div>
                                                <ul className="upgrade-benefits">
                                                    {upgrade.benefits.map((benefit, idx) => (
                                                        <li key={idx}>✓ {benefit}</li>
                                                    ))}
                                                </ul>
                                                <button
                                                    className="upgrade-btn"
                                                    onClick={() => handleUpgradeClick(upgrade)}
                                                >
                                                    Upgrade Now
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Regular Plan Upgrades (only show if no recommendations) */}
                            {(!upgradeOptions.recommendations || upgradeOptions.recommendations.length === 0) && (
                                <div className="regular-upgrades">
                                    <h4>Or Switch to Regular Plans</h4>
                                    <p className="upgrade-note">Get unlimited storage and seats with our regular subscription plans</p>
                                    <div className="upgrade-cards">
                                        {upgradeOptions.regularUpgrades.map((plan, index) => (
                                            <div key={index} className="upgrade-card regular">
                                                <div className="upgrade-header">
                                                    <h5>{plan.plan}</h5>
                                                    <div className="upgrade-price">{plan.price}</div>
                                                    <div className="upgrade-subtitle">Per month</div>
                                                    {plan.recommended && (
                                                        <div className="recommended-badge">{plan.recommended}</div>
                                                    )}
                                                </div>
                                                <ul className="upgrade-benefits">
                                                    {plan.benefits.map((benefit, idx) => (
                                                        <li key={idx}>✓ {benefit}</li>
                                                    ))}
                                                </ul>
                                                {plan.specialNote && (
                                                    <div className="special-note">{plan.specialNote}</div>
                                                )}
                                                <button
                                                    className="upgrade-btn"
                                                    onClick={() => window.location.href = `/w/${workspaceId}/billing`}
                                                >
                                                    Switch to {plan.plan}
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Non-AppSumo Users */}
            {!upgradeOptions && storageStatus.isNearLimit && (
                <div className="upgrade-section">
                    <h3>Need More Storage?</h3>
                    <p>Upgrade to a premium plan for unlimited storage and advanced features.</p>
                    <button 
                        className="upgrade-btn"
                        onClick={() => window.location.href = `/w/${workspaceId}/billing`}
                    >
                        View Plans
                    </button>
                </div>
            )}
        </div>
    );
};

export default StorageStatus;
